import { ScrollTextIcon } from '@/components/atoms/icons';
import { Accordion, DocumentItem, OkDialog } from '@/components/molecules';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useAppStore } from '@/core/stores/appStore';
import { downloadBrochureFiles } from '@/core/utils/brochureDownload';
import { shareFile } from '@/core/wrappers/sharing';
import { DOCUMENT_TYPES, PropertyDocument, type DocumentType } from '@/modules/profile/constants/documentTypes';
import { downloadAndPersistDocument } from '@/modules/property-details/utils/documentDownload';
import { getPropertyDocuments } from '@/modules/property-details/utils/propertyDocuments';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

const BROCHURE_DOC_TYPE = 'BROCHURE';

export interface PropertyDocumentsAccordionWebProps {
    salesOrderId: string; // salesorder_id from property (required for property documents)
    unitId?: string;
    unitLabel?: string;
    documents: PropertyDocument;
    /** Contentful brochure URL(s) — single file or zip when multiple. */
    brochureUrls?: ReadonlyArray<string>;
    /** Used for `{projectName}-brochure.{ext|zip}` download naming. */
    projectName?: string;
}

export function PropertyDocumentsAccordionWeb({
    salesOrderId,
    unitId,
    unitLabel,
    documents,
    brochureUrls = [],
    projectName,
}: Readonly<PropertyDocumentsAccordionWebProps>) {
    const { t } = useTranslation();
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const user = useAppStore((state) => state.auth.user);
    const customerId = user?.customer_id;

    const [downloadingDocType, setDownloadingDocType] = useState<string | null>(null);
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [savedFileUri, setSavedFileUri] = useState<string | null>(null);
    const [savedFileName, setSavedFileName] = useState<string>('');

    const propertyDocuments = getPropertyDocuments(documents);
    const resolvedBrochureUrls = useMemo(
        () => brochureUrls.map((url) => url.trim()).filter(Boolean),
        [brochureUrls],
    );
    const hasBrochure = resolvedBrochureUrls.length > 0;

    const displayedCount = propertyDocuments.length + (hasBrochure ? 1 : 0);

    if (displayedCount === 0) {
        return null;
    }

    const handleBrochurePress = async () => {
        if (!hasBrochure || downloadingDocType) {
            return;
        }
        setDownloadingDocType(BROCHURE_DOC_TYPE);
        try {
            await downloadBrochureFiles({
                urls: resolvedBrochureUrls,
                projectName: projectName?.trim() || unitLabel?.trim() || 'project',
            });
        } catch (error: unknown) {
            const message =
                error instanceof Error ? error.message : String(error);
            logger.error('PropertyDocumentsAccordionWeb', 'Failed to download brochure', {
                error: message,
            });
            setErrorMessage(t('propertyDetails.brochureDownloadFailed'));
            setErrorDialogVisible(true);
        } finally {
            setDownloadingDocType(null);
        }
    };

    const handleDocumentPress = async (docType: DocumentType, translationKey: string) => {
        if (!customerId) {
            setErrorMessage('Customer ID not available');
            setErrorDialogVisible(true);
            return;
        }
        if (downloadingDocType) {
            return;
        }

        setDownloadingDocType(docType);

        try {
            let parentId: string = '';
            if (docType === DOCUMENT_TYPES.FLOOR_PLAN && unitId) {
                parentId = unitId;
            } else {
                parentId = salesOrderId;
            }
            logger.info('PropertyDocumentsAccordion', 'Downloading document', {
                docType,
                salesOrderId,
                customerId,
            });

            const downloadResult = await downloadAndPersistDocument({
                docType: docType as any,
                parentId: parentId,
                customerId,
                translationKey: translationKey as string,
                t: t as any,
            });

            if (downloadResult.success && downloadResult.fileUri && downloadResult.message) {
                setSuccessMessage(downloadResult.message);
                setSavedFileUri(downloadResult.fileUri);
                setSavedFileName(downloadResult.fileName || '');
                setSuccessDialogVisible(true);
            } else {
                setErrorMessage(downloadResult.error || 'Failed to save file');
                setErrorDialogVisible(true);
            }
        } catch (error: any) {
            logger.error('PropertyDocumentsAccordion', 'Failed to download document', {
                docType,
                error: error?.message || error,
            });
            setErrorMessage(error?.message || 'Failed to download document. Please try again.');
            setErrorDialogVisible(true);
        } finally {
            setDownloadingDocType(null);
        }
    };

    return (
        <>
            <Accordion
                icon={ScrollTextIcon}
                title={t('propertyDetails.documents')}
                subtitle={t('propertyDetails.documentsAvailable').replace(
                    '{count}',
                    displayedCount.toString(),
                )}
                contentPadding={16}
                defaultExpanded={false}
            >
                <View style={styles.documentsList}>
                    {hasBrochure ? (
                        <View style={styles.documentItemWrapper}>
                            <DocumentItem
                                id={BROCHURE_DOC_TYPE}
                                title={t('propertyDetails.brochure')}
                                type="PDF"
                                downloadUrl=""
                                onPress={() => {
                                    if (!downloadingDocType) {
                                        void handleBrochurePress();
                                    }
                                }}
                            />
                            {downloadingDocType === BROCHURE_DOC_TYPE ? (
                                <View
                                    style={[
                                        styles.loadingBesideIcon,
                                        isRTL
                                            ? styles.loadingBesideIconRTL
                                            : styles.loadingBesideIconLTR,
                                        { backgroundColor: colors.backgroundLevel1 },
                                    ]}
                                    pointerEvents="none"
                                >
                                    <ActivityIndicator size="small" color={colors.fontPrimary} />
                                </View>
                            ) : null}
                        </View>
                    ) : null}

                    {propertyDocuments.map((documentDetails) => {
                        const isDownloading = downloadingDocType === documentDetails.docType;
                        const docTitle =
                            t(documentDetails.translationKey as any) || documentDetails.docType;
                        return (
                            <View key={documentDetails.docType} style={styles.documentItemWrapper}>
                                <DocumentItem
                                    id={documentDetails.docType}
                                    title={docTitle}
                                    type="PDF"
                                    downloadUrl=""
                                    onPress={() =>
                                        !isDownloading &&
                                        !downloadingDocType &&
                                        handleDocumentPress(
                                            documentDetails.docType,
                                            documentDetails.translationKey,
                                        )
                                    }
                                />
                                {isDownloading ? (
                                    <View
                                        style={[
                                            styles.loadingBesideIcon,
                                            isRTL
                                                ? styles.loadingBesideIconRTL
                                                : styles.loadingBesideIconLTR,
                                            { backgroundColor: colors.backgroundLevel1 },
                                        ]}
                                        pointerEvents="none"
                                    >
                                        <ActivityIndicator
                                            size="small"
                                            color={colors.fontPrimary}
                                        />
                                    </View>
                                ) : null}
                            </View>
                        );
                    })}
                </View>
            </Accordion>

            <OkDialog
                visible={errorDialogVisible}
                message={errorMessage}
                onOk={() => setErrorDialogVisible(false)}
            />

            <OkDialog
                visible={successDialogVisible}
                title={t('common.downloadSuccessTitle')}
                message={successMessage}
                okText={Platform.OS === 'ios' ? t('common.saveShare') : t('common.ok')}
                onOk={async () => {
                    if (Platform.OS === 'ios' && savedFileUri) {
                        const shareResult = await shareFile({ uri: savedFileUri });
                        if (!shareResult.success && shareResult.error) {
                            logger.warn('PropertyDocumentsAccordion', 'Sharing failed', {
                                error: shareResult.error,
                                fileUri: savedFileUri,
                            });
                        }
                    }
                    setSuccessDialogVisible(false);
                    setSavedFileUri(null);
                    setSavedFileName('');
                }}
            />
        </>
    );
}

const styles = StyleSheet.create({
    documentsList: {
        gap: 8,
    },
    documentItemWrapper: {
        position: 'relative',
    },
    /** Small spinner near the trailing download affordance (not a full-row overlay). */
    loadingBesideIcon: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 4,
        borderRadius: 8,
        opacity: 0.95,
    },
    loadingBesideIconLTR: {
        right: 48,
    },
    loadingBesideIconRTL: {
        left: 48,
    },
});

export default PropertyDocumentsAccordionWeb;
